import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import "../../_css/AIFeedback.css";
import { formatDateLong, formatGrade } from "../helper/formatters";
import { getStudentReport } from "../../requests";
import { notifyError } from "../helper/Toaster";
import { deleteAuthToken } from "../../utils/helper";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import Header from "../common/Header";
import ValidationFindingsModal from "../UI/ValidationFindingsModal";

const StudentReport = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const grade = searchParams.get("grade") ?? "";
  const exam = searchParams.get("exam") ?? "";

  const [page, setPage] = useState(1);
  const [limit] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [rows, setRows] = useState([]);
  const [nameInput, setNameInput] = useState("");
  const [searchName, setSearchName] = useState("");
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [selectedAnswerSheetId, setSelectedAnswerSheetId] = useState(null);

  useEffect(() => {
    const id = setTimeout(() => setSearchName(nameInput.trim()), 1500);
    return () => clearTimeout(id);
  }, [nameInput]);

  useEffect(() => {
    setNameInput("");
    setSearchName("");
  }, [grade, exam]);

  useEffect(() => {
    setPage(1);
  }, [grade, exam, searchName]);

  useEffect(() => {
    if (!grade || !exam) {
      setRows([]);
      setTotalCount(0);
      return;
    }

    const load = async () => {
      dispatch(startLoader());
      try {
        const queryParams = {
          page,
          limit,
          grade,
          exam_name: exam,
        };
        if (searchName) {
          queryParams.name = searchName;
        }
        const res = await getStudentReport({}, queryParams);
        const payload = res?.data;
        setRows(Array.isArray(payload?.data) ? payload.data : []);
        setTotalCount(Number(payload?.totalCount) || 0);
      } catch (error) {
        setRows([]);
        setTotalCount(0);
        if (error?.response?.status === 403) {
          notifyError(
            error?.response?.data?.error ||
              "Your session has expired. Please log in again."
          );
          deleteAuthToken();
          navigate("/login");
          return;
        }
        if (error?.response?.status === 401) {
          notifyError(
            error?.response?.data?.error ||
              "You are not authorized to access this page."
          );
          return;
        }
        notifyError(
          error?.response?.data?.message ||
            error?.response?.data?.error ||
            "Error fetching students."
        );
      } finally {
        dispatch(stopLoader());
      }
    };

    load();
  }, [grade, exam, page, limit, searchName, dispatch, navigate]);

  const handleClick = (e) => {
    const q = new URLSearchParams({
      grade: String(e.grade ?? ""),
      exam: String(e.exam_name ?? ""),
      student_name: String(e.student_name ?? ""),
    });
    navigate(`/student-reports/students/list?${q.toString()}`);
  };

  const scoreLabel = (row) => {
    const o = row.obt_marks;
    const t = row.total_marks;
    if (o != null && t != null) {
      return `${Number(o)} / ${Number(t)}`;
    }
    return "—";
  };

  const handleActionRequired = (row) => {
    setSelectedAnswerSheetId(row.id);
    setShowValidationModal(true);
  };

  const handleModalClose = () => {
    setShowValidationModal(false);
    setSelectedAnswerSheetId(null);
  };

  const handleModalSuccess = () => {
    // Reload the list to reflect updated status
    setPage(1);
    const load = async () => {
      dispatch(startLoader());
      try {
        const queryParams = {
          page,
          limit,
          grade,
          exam_name: exam,
        };
        if (searchName) {
          queryParams.name = searchName;
        }
        const res = await getStudentReport({}, queryParams);
        const payload = res?.data;
        setRows(Array.isArray(payload?.data) ? payload.data : []);
        setTotalCount(Number(payload?.totalCount) || 0);
      } catch (error) {
        // Handle error silently or show notification
      } finally {
        dispatch(stopLoader());
      }
    };
    load();
  };

  return (
    <div className="page-main">
      <Header name={["Student Reports", "Students"]} />
      <div className="ai-fd-main">
        <div className="ai-fd-con ai-fd-con--align-stretch">
          <div
            className="ai-fd-head"
            style={{
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <div
              className="ai-fb-page-context"
              style={{ flex: "1 1 auto", width: "auto", minWidth: 0 }}
            >
              {grade || exam ? (
                <>
                  Grade{" "}
                  <span className="ai-fb-context-strong">{grade || "—"}</span>
                  <span className="ai-fb-context-sep"> · </span>
                  Exam{" "}
                  <span className="ai-fb-context-strong">{exam || "—"}</span>
                </>
              ) : (
                <span className="ai-fb-context-muted">
                  Open this list from Student Reports (View students) with a
                  grade and exam selected.
                </span>
              )}
            </div>
            {grade && exam ? (
              <div className="ai-fd-right">
                <input
                  className="student-list-search"
                  style={{ height: "36px", minWidth: 220 }}
                  placeholder="Search name"
                  name="name"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                />
              </div>
            ) : null}
          </div>

          <div className="ai-fb-table ai-fb-table-simple">
            <div className="ai-fb-table-head">
              <div className="ai-fb-head-cell" style={{ width: "6%" }}>
                S.No
              </div>
              <div className="ai-fb-head-cell" style={{ width: "20%" }}>
                Student name
              </div>
              <div className="ai-fb-head-cell" style={{ width: "12%" }}>
                Score
              </div>
              <div className="ai-fb-head-cell" style={{ width: "8%" }}>
                Grade
              </div>
              <div className="ai-fb-head-cell" style={{ width: "10%" }}>
                Roll no
              </div>
              <div className="ai-fb-head-cell" style={{ width: "16%" }}>
                Exam
              </div>
              <div className="ai-fb-head-cell" style={{ width: "14%" }}>
                Last activity
              </div>
              <div
                className="ai-fb-head-cell ai-fb-cell-center"
                style={{ width: "14%" }}
              >
                Action
              </div>
            </div>

            {!grade || !exam ? (
              <div className="ai-fb-empty">
                Use Student Reports to pick a grade and exam, then choose View
                students.
              </div>
            ) : rows.length === 0 ? (
              <div className="ai-fb-empty">
                No students found for this grade and exam.
              </div>
            ) : null}

            {rows.map((row, index) => (
              <div
                key={`${row.student_name}-${row.student_rollno}-${row.exam_name}-${index}`}
                className="ai-fb-group"
              >
                <div className="ai-fb-main-row">
                  <div className="ai-fb-row-cell" style={{ width: "6%" }}>
                    {(page - 1) * limit + index + 1}
                  </div>
                  <div className="ai-fb-row-cell" style={{ width: "20%" }}>
                    {row.student_name ?? "—"}
                  </div>
                  <div className="ai-fb-row-cell" style={{ width: "12%" }}>
                    {scoreLabel(row)}
                  </div>
                  <div className="ai-fb-row-cell" style={{ width: "8%" }}>
                    {formatGrade(row.grade)}
                  </div>
                  <div className="ai-fb-row-cell" style={{ width: "10%" }}>
                    {row.student_rollno != null && row.student_rollno !== ""
                      ? row.student_rollno
                      : "—"}
                  </div>
                  <div className="ai-fb-row-cell" style={{ width: "16%" }}>
                    {row.exam_name ?? "—"}
                  </div>
                  <div className="ai-fb-row-cell" style={{ width: "14%" }}>
                    {row.last_uploaded
                      ? formatDateLong(row.last_uploaded)
                      : "—"}
                  </div>
                  <div
                    className="ai-fb-row-cell ai-fb-cell-center"
                    style={{ width: "14%" }}
                  >
                    {row.status === 3 ? (
                      <button
                        type="button"
                        className="ai-fb-action-required-btn"
                        onClick={() => handleActionRequired(row)}
                        style={{
                          backgroundColor: "#FEF3C7",
                          color: "#92400E",
                          border: "1px solid #F59E0B",
                          fontWeight: "600"
                        }}
                      >
                        Action Required
                      </button>
                    ) : row.status === 4 ? (
                      <span style={{ color: "#DC2626", fontSize: "13px", fontWeight: "500" }}>
                        Cancelled
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="ai-fb-view-all-btn"
                        onClick={() => handleClick(row)}
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {grade && exam ? (
            <div
              className="di-flex"
              style={{
                width: "100%",
                marginTop: 16,
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div style={{ color: "#6B7280" }}>
                Showing {(page - 1) * limit + 1} to{" "}
                {rows.length + (page - 1) * limit} out of {totalCount} items
              </div>
              <div
                className="di-flex"
                style={{ justifyContent: "end", gap: "10px" }}
              >
                <button
                  disabled={page === 1}
                  className="my-btn"
                  style={{
                    backgroundColor: "#E0F5FE",
                    color: "#01A7DE",
                    opacity: page === 1 ? 0.4 : 1,
                  }}
                  onClick={() => setPage((p) => (p > 1 ? p - 1 : p))}
                >
                  Previous
                </button>
                <button
                  disabled={rows.length + (page - 1) * limit >= totalCount}
                  className="my-btn"
                  style={{
                    backgroundColor: "#E0F5FE",
                    color: "#01A7DE",
                    opacity:
                      rows.length + (page - 1) * limit >= totalCount
                        ? 0.4
                        : 1,
                  }}
                  onClick={() => setPage((p) => (rows.length ? p + 1 : p))}
                >
                  Next
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Validation Findings Modal */}
      {showValidationModal && selectedAnswerSheetId && (
        <ValidationFindingsModal
          answersheetId={selectedAnswerSheetId}
          fn={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default StudentReport;
